import { useMemo, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { DEFAULT_SETTINGS, esCuentaDeBanco, hoy, type Centavos } from '@abastece/shared';
import type { MovimientoDueno } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { cuentasDeLaTienda, nombreDeCuenta } from '../lib/bancos.js';
import { rangoDe } from '../lib/historial.js';
import {
  SIN_FILTROS_DUENO,
  TIPOS_MOVIMIENTO,
  VIGENCIAS,
  filtrosDeDueno,
  movimientoCoincide,
  ordenarMovimientos,
  resumenDueno,
  type FiltroDueno,
  type TipoMovimiento,
  type Vigencia,
} from '../lib/historialDinero.js';
import { useMonedaDeLosLibros } from '../lib/libros.js';
import { usePuede } from '../lib/sesion.js';
import { AnularMovimiento } from '../ui/Correcciones.js';
import { FilaMoneda } from '../ui/ElegirMoneda.js';
import { FilaMovimientoDueno, diaDeLista } from '../ui/FilasDeDinero.js';
import { Desplegable, FilaDesplegables, FiltroFechas, FiltrosEnLinea } from '../ui/FiltrosHistorial.js';
import { Ico } from '../ui/Iconos.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { useT, type Clave } from '../i18n/index.js';

/** How many show at a time: the latest, and «Ver más» for the rest. */
const LOTE = 50;

/** Where the owner's money came out of or went into, besides the bank accounts, as the money screens name each. */
const NOMBRE_CUENTA: Record<string, Clave> = {
  CAJA_FUERTE: 'efectivo.general',
  BILLETERA_MOVIL: 'efectivo.billetera',
};

/** What a movement was, as its form asks it: the owner took money out, or put it in. */
const NOMBRE_TIPO: Record<TipoMovimiento, Clave> = { todos: 'duenoHist.tipoTodos', retiro: 'gastos.retiro', aporte: 'gastos.aporte' };

/**
 * «Dinero del dueño», all of it: every time the owner took money out of the shop or put money in,
 * found by day, by which, by where from and by what it was for. «Gastos» keeps the latest twenty.
 */
export function DineroDueno() {
  const t = useT();
  const puedeAnular = usePuede('configurar');
  /** Córdobas or dollars, as «Gastos» is read (2026-10-13). */
  const libros = useMonedaDeLosLibros();
  const enFila = libros.moneda === 'USD' ? (c: Centavos) => libros.dinero(c) : undefined;
  const hoyEs = hoy();
  const movimientos = useLiveQuery(() => db.movimientosDueno.toArray(), [], undefined);
  const [filtro, setFiltro] = useState<FiltroDueno>(SIN_FILTROS_DUENO);
  const [cuantos, setCuantos] = useState(LOTE);
  const [anulando, setAnulando] = useState<MovimientoDueno | null>(null);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  /** General cash, each of the shop's bank accounts, the wallet: what the books name each by. */
  const cuentas = ['CAJA_FUERTE', ...cuentasDeLaTienda(config).map((c) => c.cuenta), 'BILLETERA_MOVIL'];
  const nombreCuenta = (cuenta: string): string | null =>
    esCuentaDeBanco(cuenta) ? nombreDeCuenta(config, cuenta, t) : NOMBRE_CUENTA[cuenta] ? t(NOMBRE_CUENTA[cuenta]) : null;

  const rango = rangoDe(filtro.periodo, hoyEs, filtro);
  const puestos = filtrosDeDueno(filtro, rango);
  const filtrando = filtro.texto.trim() !== '' || puestos.length > 0;
  const cambiar = (cambio: Partial<FiltroDueno>) => {
    setFiltro((f) => ({ ...f, ...cambio }));
    setCuantos(LOTE);
  };
  const encontrados = useMemo(
    () => ordenarMovimientos((movimientos ?? []).filter((m) => movimientoCoincide(m, filtro, rango))),
    // `rango` is worked out afresh each time: its two days are what it is.
    [movimientos, filtro, rango.desde, rango.hasta],
  );
  const resumen = resumenDueno(encontrados);
  const cuantosHay = movimientos?.length ?? 0;

  return (
    <Pagina titulo={t('gastos.dineroDelDueno')} subtitulo={t('duenoHist.registrados', { n: cuantosHay })} volverA="/gastos" volverBusqueda={{ vista: 'dueno' }}>
      <FilaMoneda moneda={libros.moneda} onElegir={libros.elegir} tasa={libros.tasa} />
      {(filtrando || cuantosHay > 0) && (
        <FiltrosEnLinea
          buscar={{ texto: filtro.texto, onTexto: (texto) => cambiar({ texto }), placeholder: t('duenoHist.buscar') }}
          hayPuestos={puestos.length > 0}
          onQuitarTodos={() => cambiar({ ...SIN_FILTROS_DUENO, texto: filtro.texto })}
        >
          <FiltroFechas periodo={filtro.periodo} desde={filtro.desde} hasta={filtro.hasta} onCambio={cambiar} />
          <FilaDesplegables>
            <Desplegable
              clave="tipo"
              etiqueta={t('gastos.movimiento')}
              value={filtro.tipo}
              onChange={(v) => cambiar({ tipo: v as TipoMovimiento })}
              elegido={filtro.tipo === 'todos' ? undefined : t(NOMBRE_TIPO[filtro.tipo])}
            >
              {TIPOS_MOVIMIENTO.map((tipo) => (
                <option key={tipo} value={tipo}>
                  {t(NOMBRE_TIPO[tipo])}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="cuenta"
              etiqueta={t('duenoHist.cuenta')}
              value={filtro.cuenta}
              onChange={(cuenta) => cambiar({ cuenta })}
              elegido={filtro.cuenta ? (nombreCuenta(filtro.cuenta) ?? t('efectivo.general')) : undefined}
            >
              <option value="">{t('historial.todas')}</option>
              {cuentas.map((cuenta) => (
                <option key={cuenta} value={cuenta}>
                  {nombreCuenta(cuenta)}
                </option>
              ))}
            </Desplegable>
            <Desplegable
              clave="estado"
              etiqueta={t('historial.estado')}
              value={filtro.estado}
              onChange={(v) => cambiar({ estado: v as Vigencia })}
              elegido={filtro.estado === 'todos' ? undefined : t(`historial.vigencia.${filtro.estado}`)}
            >
              {VIGENCIAS.map((v) => (
                <option key={v} value={v}>
                  {t(`historial.vigencia.${v}`)}
                </option>
              ))}
            </Desplegable>
          </FilaDesplegables>
        </FiltrosEnLinea>
      )}

      {filtrando && encontrados.length > 0 && (
        <p className="tabular mb-3 text-sm text-slate-400" data-historial-resumen>
          {t('duenoHist.resumen', { n: resumen.n, retiros: libros.dinero(resumen.retiros), aportes: libros.dinero(resumen.aportes) })}
        </p>
      )}

      {/* Nothing said while it is read: «nothing matches» was only «not read yet». */}
      {movimientos === undefined ? null : encontrados.length === 0 ? (
        filtrando ? (
          <Vacio mensaje={t('duenoHist.ninguno')} icono="buscar">
            <button type="button" className="btn-secundario" onClick={() => cambiar(SIN_FILTROS_DUENO)}>
              <Ico icono="reiniciar" />
              {t('historial.quitarBusqueda')}
            </button>
          </Vacio>
        ) : (
          <Vacio mensaje={t('duenoHist.vacio')} icono="retirar" />
        )
      ) : (
        <ul className="tarjeta flex flex-col divide-y divide-slate-800 py-1 text-sm" data-historial-lista>
          {encontrados.slice(0, cuantos).map((m) => {
            const cuenta = nombreCuenta(m.cuenta);
            return (
              <FilaMovimientoDueno
                key={m.id}
                movimiento={m}
                detalle={[diaDeLista(m.fecha), cuenta ?? ''].filter(Boolean).join(' · ')}
                className="py-2.5"
                {...(enFila ? { dinero: enFila } : {})}
                {...(puedeAnular ? { onAnular: () => setAnulando(m) } : {})}
              />
            );
          })}
        </ul>
      )}
      {encontrados.length > cuantos && (
        <button type="button" className="btn-secundario mt-4 w-full" onClick={() => setCuantos((n) => n + LOTE)}>
          <Ico icono="desplegar" />
          {t('comun.verMas')}
        </button>
      )}

      {anulando && <AnularMovimiento movimiento={anulando} onCerrar={() => setAnulando(null)} />}
    </Pagina>
  );
}
